'use client';

import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faSearch, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { twMerge } from 'tailwind-merge';
import { Chip } from '../Chip';
import { CircularProgress } from '../CircularProgress';
import { ClickAwayListener } from '../ClickAwayListener';

export interface PlacesSearchAutocompleteOption {
  /** Unique identifier for the option */
  value: string;
  /** Primary display label */
  label: string;
  /** Secondary label shown below the primary label */
  secondaryLabel?: string;
  /** Type badge shown as a Chip */
  type?: 'city' | 'county' | 'state' | 'address';
}

export interface PlacesSearchAutocompleteProps {
  /** Options to display in the dropdown */
  options: PlacesSearchAutocompleteOption[];
  /** Currently selected option */
  value: PlacesSearchAutocompleteOption | null;
  /** Callback when an option is selected */
  onChange: (option: PlacesSearchAutocompleteOption | null) => void;
  /** Controlled input value */
  inputValue?: string;
  /** Callback when input value changes */
  onInputChange?: (value: string) => void;
  /** Whether the dropdown is loading */
  loading?: boolean;
  /** Text to display while loading */
  loadingText?: string;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Text to display when no results are found */
  emptyText?: string;
  /** Additional className for the container */
  className?: string;
  /** Whether to start expanded */
  defaultExpanded?: boolean;
  /** Callback when expanded state changes */
  onExpandedChange?: (expanded: boolean) => void;
}

const COLLAPSED_WIDTH = 48;
const EXPANDED_WIDTH = 400;
const ANIMATION_DURATION = 0.2;

const typeLabels: Record<NonNullable<PlacesSearchAutocompleteOption['type']>, string> = {
  city: 'City',
  county: 'County',
  state: 'State',
  address: 'Address',
};

const typeColors: Record<NonNullable<PlacesSearchAutocompleteOption['type']>, 'primary' | 'success' | 'warning' | 'magenta'> = {
  city: 'primary',
  county: 'success',
  state: 'warning',
  address: 'magenta',
};

const PlacesSearchAutocomplete = ({
  options,
  onChange,
  inputValue: controlledInputValue,
  onInputChange,
  loading = false,
  loadingText = 'Searching...',
  placeholder = 'Search...',
  emptyText = 'No results found',
  className = '',
  defaultExpanded = false,
  onExpandedChange,
}: PlacesSearchAutocompleteProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [internalInputValue, setInternalInputValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [isDropdownPositionReady, setIsDropdownPositionReady] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isControlled = controlledInputValue !== undefined;
  const currentInputValue = isControlled ? controlledInputValue : internalInputValue;

  const handleExpand = useCallback(() => {
    if (!isExpanded) {
      setIsExpanded(true);
      onExpandedChange?.(true);
    }
  }, [isExpanded, onExpandedChange]);

  const handleCollapse = useCallback(() => {
    if (isExpanded) {
      setIsExpanded(false);
      setIsDropdownOpen(false);
      onExpandedChange?.(false);
    }
  }, [isExpanded, onExpandedChange]);

  const handleClickAway = useCallback(() => {
    handleCollapse();
  }, [handleCollapse]);

  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isControlled) {
      setInternalInputValue('');
    }
    onInputChange?.('');
    onChange(null);
    inputRef.current?.focus();
  }, [isControlled, onInputChange, onChange]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (!isControlled) {
      setInternalInputValue(newValue);
    }
    onInputChange?.(newValue);
    if (newValue && !isDropdownOpen) {
      setIsDropdownOpen(true);
    }
    if (!newValue) {
      setIsDropdownOpen(false);
    }
    setHighlightedIndex(-1);
  }, [isControlled, onInputChange, isDropdownOpen]);

  const handleOptionSelect = useCallback((option: PlacesSearchAutocompleteOption) => {
    onChange(option);
    if (!isControlled) {
      setInternalInputValue(option.label);
    }
    onInputChange?.(option.label);
    setIsDropdownOpen(false);
    setHighlightedIndex(-1);
  }, [onChange, isControlled, onInputChange]);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      handleCollapse();
      return;
    }

    if (!isDropdownOpen || options.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < options.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < options.length) {
          handleOptionSelect(options[highlightedIndex]);
        }
        break;
    }
  }, [handleCollapse, isDropdownOpen, options, highlightedIndex, handleOptionSelect]);

  const updateDropdownPosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const scrollX = window.scrollX || document.documentElement.scrollLeft;
      const scrollY = window.scrollY || document.documentElement.scrollTop;

      setDropdownPosition({
        top: rect.bottom + scrollY + 4,
        left: rect.left + scrollX,
        width: EXPANDED_WIDTH,
      });
      setIsDropdownPositionReady(true);
    }
  }, []);

  // Focus input when expanded
  useEffect(() => {
    if (isExpanded) {
      // Small delay to ensure animation has started
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isExpanded]);

  // Update dropdown position when open
  useEffect(() => {
    if (isDropdownOpen && isExpanded) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
    }

    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [isDropdownOpen, isExpanded, updateDropdownPosition]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.querySelector(
        `[data-index="${highlightedIndex}"]`
      );
      highlightedElement?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  // Apply dynamic positioning styles via ref to avoid inline style prop
  useEffect(() => {
    if (dropdownRef.current && isDropdownPositionReady) {
      dropdownRef.current.style.top = `${dropdownPosition.top}px`;
      dropdownRef.current.style.left = `${dropdownPosition.left}px`;
      dropdownRef.current.style.width = `${dropdownPosition.width}px`;
    }
  }, [dropdownPosition, isDropdownPositionReady]);

  const showDropdown = isExpanded && isDropdownOpen && isDropdownPositionReady && (
    loading || options.length > 0 || currentInputValue.length > 0
  );

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div ref={containerRef} className={twMerge('relative', className)}>
        <motion.div
          initial={false}
          animate={{
            width: isExpanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
          }}
          transition={{
            duration: ANIMATION_DURATION,
            ease: 'easeOut',
          }}
          className={twMerge(
            'h-12 bg-white shadow-md rounded-full border border-neutral-200',
            'relative overflow-hidden',
            !isExpanded && 'cursor-pointer'
          )}
          onClick={!isExpanded ? handleExpand : undefined}
          data-testid="search-autocomplete-container"
        >
          {/* Search Icon - Always positioned at left */}
          <div className="absolute left-0 top-0 bottom-0 w-12 flex items-center justify-center shrink-0">
            <Icon
              icon={faSearch}
              className="w-4 h-4 text-neutral-500"
              data-testid="search-icon"
            />
          </div>

          {/* Input - Only visible when expanded, positioned after the icon */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.1 }}
                className={twMerge(
                  'absolute left-12 top-0 bottom-0 flex items-center',
                  currentInputValue ? 'right-12' : 'right-4'
                )}
              >
                <input
                  ref={inputRef}
                  type="text"
                  value={currentInputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder={placeholder}
                  className="flex-1 min-w-0 bg-transparent outline-none text-sm text-neutral-800 placeholder-neutral-400"
                  data-testid="search-input"
                  aria-label="Search"
                  aria-expanded={isDropdownOpen}
                  aria-autocomplete="list"
                  aria-controls={isDropdownOpen ? 'places-search-listbox' : undefined}
                  role="combobox"
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Clear Button - Only visible when expanded and has value, positioned at right */}
          <AnimatePresence>
            {isExpanded && currentInputValue && (
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.1 }}
                type="button"
                onClick={handleClear}
                className="absolute right-0 top-0 bottom-0 w-12 flex items-center justify-center text-neutral-500 hover:text-neutral-700"
                aria-label="Clear search"
                data-testid="clear-button"
              >
                <Icon icon={faXmark} className="w-4 h-4" />
              </motion.button>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Dropdown Portal */}
        {showDropdown &&
          createPortal(
            <div
              ref={dropdownRef}
              data-portal-content
              className="fixed z-[9999] bg-white border border-neutral-200 rounded-lg shadow-lg overflow-hidden"
              data-testid="search-dropdown"
              id="places-search-listbox"
              role="listbox"
            >
              <div className="max-h-[240px] overflow-y-auto">
                {loading ? (
                  <div
                    className="flex items-center gap-2 px-4 py-3 text-sm text-neutral-600"
                    data-testid="loading-state"
                  >
                    <CircularProgress size="sm" />
                    <span>{loadingText}</span>
                  </div>
                ) : options.length === 0 ? (
                  <div
                    className="px-4 py-3 text-sm text-neutral-500"
                    data-testid="empty-state"
                  >
                    {emptyText}
                  </div>
                ) : (
                  options.map((option, index) => (
                    <div
                      key={option.value}
                      data-index={index}
                      onClick={() => handleOptionSelect(option)}
                      className={twMerge(
                        'flex items-center justify-between px-4 py-2 cursor-pointer transition-colors',
                        highlightedIndex === index ? 'bg-neutral-100' : 'hover:bg-neutral-50'
                      )}
                      role="option"
                      aria-selected={highlightedIndex === index}
                      data-testid={`option-${option.value}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-neutral-800 truncate">
                          {option.label}
                        </div>
                        {option.secondaryLabel && (
                          <div className="text-xs text-neutral-500 truncate">
                            {option.secondaryLabel}
                          </div>
                        )}
                      </div>
                      {option.type && (
                        <Chip
                          size="sm"
                          variant="soft"
                          color={typeColors[option.type]}
                          className="ml-2 shrink-0"
                        >
                          {typeLabels[option.type]}
                        </Chip>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>,
            document.body
          )}
      </div>
    </ClickAwayListener>
  );
};

PlacesSearchAutocomplete.displayName = 'PlacesSearchAutocomplete';

export default PlacesSearchAutocomplete;
